import { CaretUpDownIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import filter from 'lodash/filter';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { GenericTable } from '@/modules/common/components';

import { GcrFileItem, GcrFormOptions, TableFiltersType } from '../types';
import { TableFilters } from './table-filters';

interface GcrsTableProps {
  data: GcrFileItem[];
  onClick: (item: GcrFileItem) => void;
  options: GcrFormOptions;
}

export function GcrsTable({ data, onClick, options }: GcrsTableProps) {
  const [filters, setFilters] = useState<Partial<TableFiltersType>>({});

  const filteredData = useMemo(() => {
    if (!Object.keys(filters).length) {
      return data;
    }

    return filter(data, (item) => {
      return Object.keys(filters).every((key) => {
        return item[key].id === filters[key];
      });
    });
  }, [data, filters]);

  const columns: ColumnDef<GcrFileItem>[] = [
    {
      accessorKey: 'name',
      header: 'File Upload',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Model Type
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.type.value}</span>
      ),
    },
    {
      accessorKey: 'size',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Size
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.size.value}</span>
      ),
    },
    {
      accessorKey: 'color',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Color
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.color.value}</span>
      ),
    },
    {
      accessorKey: 'printer',
      header: 'Printer Name',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.printer.value}</span>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Uploaded Date
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
  ];

  return (
    <>
      <TableFilters
        options={options}
        filters={filters}
        setFilters={setFilters}
      />

      <GenericTable
        data={filteredData}
        onClick={onClick}
        columns={columns}
        emptyDescription="No gcr files uploaded yet"
      />
    </>
  );
}
